import React, { useState, useRef } from "react";
import { Star, X, Camera, Loader2, CheckCircle2, Upload } from "lucide-react";
import { api, formatApiError, resolveUrl } from "../lib/api";

/**
 * Public review submission modal. Anyone can submit (no token / no auth).
 * Reviews are saved as pending and must be moderated by admin.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   targetType: "site" | "car"
 *   carId?: string
 *   carName?: string
 */
export default function PublicReviewModal({ open, onClose, targetType = "site", carId = "", carName = "" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  if (!open) return null;

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 6);
    setFiles(list);
    setPreviews(list.map((f) => ({ name: f.name, type: f.type.startsWith("video") ? "video" : "image", url: URL.createObjectURL(f) })));
  };

  const removeFile = (i) => {
    const next = files.filter((_, idx) => idx !== i);
    const nextPrev = previews.filter((_, idx) => idx !== i);
    setFiles(next);
    setPreviews(nextPrev);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (name.trim().length < 2) { alert("Numele este obligatoriu (min 2 caractere)"); return; }
    if (rating < 1 || rating > 5) { alert("Selecteaza un rating (1-5 stele)"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("guest_name", name.trim());
      fd.append("guest_email", email.trim());
      fd.append("rating", String(rating));
      fd.append("comment", comment.trim());
      fd.append("target_type", targetType);
      if (carId) fd.append("car_id", carId);
      files.forEach((f) => fd.append("files", f));
      await api.post("/reviews/public", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) {
      alert(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setName(""); setEmail(""); setRating(0); setComment(""); setFiles([]); setPreviews([]); setDone(false);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 flex items-start justify-center overflow-auto p-3 sm:p-6"
      onClick={onClose}
      data-testid="public-review-modal"
    >
      <div
        className="card-surface p-5 sm:p-8 w-full max-w-xl mt-8 sm:mt-12"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>Recenzie</div>
            <h2 className="font-serif text-2xl sm:text-3xl">
              {targetType === "car" && carName ? `Recenzie pentru ${carName}` : "Lasa o recenzie"}
            </h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Inchide" data-testid="public-review-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <div className="text-center py-8" data-testid="public-review-success">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--brand-primary)" }} />
            <h3 className="font-serif text-xl mb-2">Multumim pentru recenzie!</h3>
            <p className="text-sm mb-6" style={{ color: "var(--brand-text-secondary)" }}>
              Recenzia ta a fost inregistrata si va fi publicata dupa moderare.
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={reset} className="btn-ghost">Mai scriu una</button>
              <button onClick={onClose} className="btn-gold-filled">Inchide</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-1.5">Numele tau *</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="brand-input w-full"
                  placeholder="Ion Popescu"
                  data-testid="public-review-name"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Email (optional)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="brand-input w-full"
                  placeholder="ion@example.com"
                  data-testid="public-review-email"
                />
              </div>
            </div>

            <div>
              <label className="label-eyebrow block mb-2">Rating *</label>
              <div className="flex gap-1.5" onMouseLeave={() => setHoverRating(0)} data-testid="public-review-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setRating(s)}
                    onMouseEnter={() => setHoverRating(s)}
                    className="p-1 transition"
                    aria-label={`${s} stele`}
                    data-testid={`public-review-star-${s}`}
                  >
                    <Star
                      className="w-7 h-7 transition"
                      fill={s <= (hoverRating || rating) ? "var(--brand-primary)" : "transparent"}
                      stroke="var(--brand-primary)"
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label-eyebrow block mb-1.5">Recenzia ta (optional)</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                maxLength={2000}
                className="brand-input w-full resize-none"
                placeholder="Cum ti s-a parut serviciul? Recomanzi?"
                data-testid="public-review-comment"
              />
              <div className="text-right text-[10px] mt-1" style={{ color: "var(--brand-text-secondary)" }}>{comment.length}/2000</div>
            </div>

            <div>
              <label className="label-eyebrow block mb-1.5">Adauga poze sau video (optional, max 6)</label>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFiles}
                className="hidden"
                data-testid="public-review-files"
              />
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="btn-ghost text-xs inline-flex items-center gap-2"
                data-testid="public-review-files-pick"
              >
                <Upload className="w-3.5 h-3.5" /> Selecteaza fisiere
              </button>
              {previews.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
                  {previews.map((p, i) => (
                    <div key={i} className="relative group" data-testid={`public-review-preview-${i}`}>
                      {p.type === "video" ? (
                        <div className="w-full aspect-square bg-black/50 flex items-center justify-center text-xs text-white rounded">
                          <Camera className="w-5 h-5" />
                        </div>
                      ) : (
                        <img src={p.url} alt="" className="w-full aspect-square object-cover rounded" />
                      )}
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="absolute -top-2 -right-2 bg-black/80 text-white rounded-full p-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="text-[11px]" style={{ color: "var(--brand-text-secondary)" }}>
              Trimitand acest formular esti de acord ca recenzia (numele si textul, fara email) sa fie afisata public dupa moderare.
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting || rating === 0 || name.trim().length < 2}
                className="btn-gold-filled flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="public-review-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                Trimite recenzia
              </button>
              <button type="button" onClick={onClose} className="btn-ghost">Anuleaza</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
